import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Send, Plus, Trash2, MessageSquare, Sparkles, AlertTriangle, Loader2 } from "lucide-react";

const STARTER_PROMPTS = [
  "I'm a 19yo NW1, low BF, weak jawline — give me a 90-day natural protocol.",
  "I'm already 4 weeks into ostarine. What bloodwork should I run and how do I PCT?",
  "Best peptide stack for skin / collagen (natural ascending only)?",
  "Hairline receding fast. Walk me through fin + min + microneedling safely.",
  "How do I dial in skinmaxx? My T-zone is oily, cheeks dry, mild acne.",
  "Frame is narrow shoulders. What lifts move the needle in 6 months?",
];

export default function Coach() {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    if (activeId) loadMessages(activeId);
    else setMessages([]);
  }, [activeId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const loadSessions = async () => {
    try {
      const { data } = await api.get("/coach/sessions");
      setSessions(data);
      if (data.length && !activeId) setActiveId(data[0].id);
    } catch {}
  };

  const loadMessages = async (sid) => {
    setLoadingMsgs(true);
    try {
      const { data } = await api.get(`/coach/sessions/${sid}/messages`);
      setMessages(data.messages || []);
    } catch {
      setMessages([]);
    } finally {
      setLoadingMsgs(false);
    }
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    setSending(true);
    setInput("");
    const optimistic = [
      ...messages,
      { id: "u-tmp", role: "user", content, created_at: new Date().toISOString() },
    ];
    setMessages(optimistic);
    try {
      const { data } = await api.post("/coach/chat", { message: content, session_id: activeId });
      if (!activeId) setActiveId(data.session_id);
      setMessages((prev) => [
        ...prev.filter((m) => m.id !== "u-tmp"),
        { id: `u-${Date.now()}`, role: "user", content, created_at: new Date().toISOString() },
        { id: `a-${Date.now()}`, role: "assistant", content: data.reply, created_at: new Date().toISOString() },
      ]);
      loadSessions();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Coach is offline");
      setMessages((prev) => prev.filter((m) => m.id !== "u-tmp"));
      setInput(content);
    } finally {
      setSending(false);
    }
  };

  const newChat = () => {
    setActiveId(null);
    setMessages([]);
    setInput("");
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this chat?")) return;
    try {
      await api.delete(`/coach/sessions/${sid}`);
      setSessions((s) => s.filter((x) => x.id !== sid));
      if (activeId === sid) newChat();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 md:py-10">
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <span className="tape-label">ASCEND COACH</span>
          <h1 className="font-display text-5xl md:text-7xl mt-3 leading-none">
            <span className="text-[var(--primary)] glow">AI COACH</span><br />
            ON DEMAND.
          </h1>
          <p className="text-[var(--text-muted)] mt-4 max-w-xl text-sm">
            Looksmaxxing strategist + harm-reduction pharma educator. Natural ascension first. If you're
            already on something — get the safe protocol, not the cope.
          </p>
        </div>
        <div className="hidden md:flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
          <AlertTriangle className="w-3.5 h-3.5 text-[var(--warning)]" />
          <span>Educational only. Always consult a real doctor.</span>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Sessions sidebar */}
        <aside className="col-span-12 md:col-span-3 panel p-3">
          <button
            onClick={newChat}
            className="btn-brutal w-full mb-3 text-sm"
            data-testid="coach-new-chat"
          >
            <Plus className="inline w-4 h-4 mr-1" /> New chat
          </button>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-2 px-2">
            History
          </div>
          <div className="space-y-1 max-h-[60vh] overflow-y-auto">
            {sessions.length === 0 && (
              <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] px-2 py-3">
                No conversations yet
              </div>
            )}
            {sessions.map((s) => (
              <button
                key={s.id}
                onClick={() => setActiveId(s.id)}
                data-testid={`coach-session-${s.id}`}
                className={`w-full text-left px-3 py-2 border-l-2 transition-colors group flex items-center justify-between gap-2 ${
                  activeId === s.id
                    ? "bg-[var(--surface-2)] border-[var(--primary)]"
                    : "border-transparent hover:bg-[var(--surface-2)]"
                }`}
              >
                <div className="flex items-center gap-2 truncate flex-1">
                  <MessageSquare className="w-3.5 h-3.5 text-[var(--primary)] shrink-0" />
                  <span className="text-xs font-mono truncate">{s.title}</span>
                </div>
                <Trash2
                  onClick={(e) => deleteSession(s.id, e)}
                  className="w-3.5 h-3.5 text-[var(--text-muted)] hover:text-[var(--danger)] opacity-0 group-hover:opacity-100 shrink-0"
                />
              </button>
            ))}
          </div>
        </aside>

        {/* Chat */}
        <div className="col-span-12 md:col-span-9 panel flex flex-col h-[75vh]">
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-6 space-y-5"
            data-testid="coach-messages"
          >
            {messages.length === 0 && !loadingMsgs && (
              <EmptyState onPick={(t) => send(t)} />
            )}
            {loadingMsgs && (
              <div className="text-center text-xs font-mono text-[var(--text-muted)] py-10">LOADING…</div>
            )}
            {messages.map((m) => (
              <MessageBubble key={m.id} role={m.role} content={m.content} />
            ))}
            {sending && (
              <div className="flex items-center gap-2 text-xs font-mono text-[var(--text-muted)]">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-[var(--primary)]" />
                <span className="flicker">COACH IS TYPING…</span>
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="border-t border-[var(--border)] p-3 flex items-end gap-2"
          >
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Ask about mewing, peptides, skincare, harm reduction…"
              rows={2}
              maxLength={2000}
              className="brutal resize-none"
              data-testid="coach-input"
            />
            <button
              type="submit"
              disabled={!input.trim() || sending}
              className="btn-brutal h-full px-4"
              data-testid="coach-send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ onPick }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center py-10">
      <Sparkles className="w-10 h-10 text-[var(--primary)] mb-4" />
      <h2 className="font-display text-4xl tracking-wide mb-2">
        WHAT DO YOU WANT TO <span className="text-[var(--primary)]">ASCEND</span>?
      </h2>
      <p className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)] mb-6">
        Pick a starter, or type your own
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-w-3xl w-full">
        {STARTER_PROMPTS.map((p, i) => (
          <button
            key={i}
            onClick={() => onPick(p)}
            data-testid={`coach-starter-${i}`}
            className="text-left text-sm border border-[var(--border-bright)] p-3 hover:border-[var(--primary)] hover:bg-[var(--surface-2)] transition-all font-mono"
          >
            {p}
          </button>
        ))}
      </div>
      <div className="mt-8 text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] flex items-center gap-2">
        <AlertTriangle className="w-3 h-3 text-[var(--warning)]" />
        Coach never recommends starting performance drugs. Harm reduction only.
      </div>
    </div>
  );
}

function MessageBubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`msg-${role}`}>
      <div
        className={`max-w-[85%] px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed border-l-2 ${
          isUser
            ? "bg-[var(--surface-2)] border-[var(--primary-dim)] text-[var(--text)]"
            : "bg-[var(--surface)] border-[var(--primary)] text-[var(--text)]"
        }`}
      >
        <div className="text-[10px] font-mono uppercase tracking-widest mb-1.5 opacity-60">
          {isUser ? "YOU" : "ASCEND COACH"}
        </div>
        {content}
      </div>
    </div>
  );
}
